import { Icon } from '@assembly-js/design-system';
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
} from '@mui/material';
import React from 'react';

import {
  SidebarListItemContainer,
  SidebarListItemIcon,
  SidebarListItemText,
} from 'src/components/sidebar/sidebar-items/SidebarListItem';
import { APP_STORE_PAGE } from 'src/constants';
import { RouteContext, useFeatureFlags } from 'src/context';
import useNavigate from 'src/hooks/useNavigate';
import { useAppSelector } from 'src/hooks/useStore';
import { useRouteAccess } from 'src/routes/routeAccess';

const addAppMenuItems = [
  {
    label: 'Build app',
    path: '/app-setup/sandbox?tab=build',
    icon: 'Hammer',
    requiresAppBuilder: true,
  },
  {
    label: 'Embed app',
    path: '/app-setup/sandbox?tab=embeds',
    icon: 'Link',
    requiresAppBuilder: false,
  },
  {
    label: 'Browse apps',
    path: '/app-setup/sandbox?tab=apps',
    icon: 'Store',
    requiresAppBuilder: false,
  },
] as const;

export const AddAppSidebarItem = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { navigate } = useNavigate();
  const isClient = useAppSelector((state) => state.user.isClient);
  const { EnableAppBuilder, EnableAppSetupImprovements } = useFeatureFlags();
  const { routeAccess } = useRouteAccess();
  const { pathname } = React.useContext(RouteContext);

  const canAccess =
    !isClient &&
    (EnableAppSetupImprovements || EnableAppBuilder) &&
    routeAccess.allowSettingsModuleManagement;

  if (!canAccess) return null;

  const visibleMenuItems = addAppMenuItems.filter(
    (item) => !item.requiresAppBuilder || EnableAppBuilder,
  );

  // Don't highlight when inside a specific builder session — that route owns
  // the corresponding draft sidebar item, which surfaces its own active state.
  const isOnAppSetupRoute =
    pathname.includes(APP_STORE_PAGE.path) ||
    (pathname.includes('/app-setup/build') &&
      !pathname.includes('/app-setup/build/session/'));

  const handleClose = () => setAnchorEl(null);

  const handleMenuItemClick = (path: string) => {
    handleClose();
    navigate(path);
  };

  return (
    <>
      <SidebarListItemContainer
        htmlId="sidebar-add-app"
        handleClick={(event) => {
          if (event?.currentTarget instanceof HTMLElement) {
            setAnchorEl(event.currentTarget);
          }
        }}
        selected={Boolean(anchorEl) || isOnAppSetupRoute}
      >
        <div className="flex items-center opacity-60">
          <SidebarListItemIcon className="min-w-6" iconName="Plus" />
          <SidebarListItemText text="Add App" />
        </div>
      </SidebarListItemContainer>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuList className="p-0" dense>
          {visibleMenuItems.map(({ label, path, icon }) => (
            <MenuItem
              key={path}
              className="py-0.5 px-3"
              onClick={() => handleMenuItemClick(path)}
            >
              <ListItemIcon className="!min-w-7">
                <Icon icon={icon} className="h-4 w-4" />
              </ListItemIcon>
              <ListItemText
                primary={label}
                primaryTypographyProps={{ fontSize: 13 }}
              />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
};
